/**
 * Engine facade.
 * Owns the renderer, scene, camera, assembly. Exposes high-level methods
 * that the UI layer calls (rebuildAssembly, applyFabric, applyLeg, etc.).
 */

import * as THREE from "three";
import { createRenderer, resizeRendererToDisplaySize } from "./Renderer";
import { setupLighting, addCameraHeadlight, ContactShadow } from "./Lighting";
import { StudioLighting } from "./StudioLighting";
import { CameraRig } from "./Camera";
import { buildAssembly, disposeAssembly, type AssemblyResult } from "./Assembly";
import { applyFabric, applyLeg, clearTextureCache, recolorByName, setMaxAnisotropy, maximizeAnisotropy } from "./Materials";
import { clearModuleCache, setPinnedModules, setModuleCacheBudget, trimModuleCache, evictModule, wireKtx2, onModuleProgress } from "./ModuleLoader";
import { isLodEnabled, proxyUrlFor, ensureLodManifest, hasProxy, setLodStage, effectiveFullUrl } from "./Lod";
import { isMobileDevice } from "@/perf/GPUTier";
import { FrameMonitor } from "@/perf/FrameLimiter";
import type { ConfigState, DeviceProfile, FabricSwatch, LegOption, ProductConfig, ViewerOptions, HotspotDef, StudioRecipe } from "@/types";

export interface EngineOptions {
  container: HTMLElement;
  profile: DeviceProfile;
  initial: ConfigState;
  /** Per-product viewer setup (camera fov/focal/zoom, exposure, shadow). */
  viewer?: ViewerOptions;
  onCameraChange?: (state: { yaw: number; pitch: number; zoom: number }) => void;
  onLowFps?: (fps: number) => void;
}

export interface MeshInfo {
  vertexCount: number;
  worldPos: [number, number, number];
  bboxSize: [number, number, number];
  inFrustum: boolean;
  fromGLB: boolean;
}

export interface ModuleInspection {
  moduleId: string;
  worldPos: [number, number, number];
  bbox: { min: [number, number, number]; max: [number, number, number] };
  meshCount: number;
  totalVertices: number;
  meshes: MeshInfo[];
}

export interface SceneInspection {
  formeId: string;
  modules: ModuleInspection[];
  gltfLoads: number;
  gltfFallbacks: number;
}

/** Reverse triangle winding in place. After a mirror matrix is baked into a
 *  geometry, applyMatrix4 points the normals the right way (inverse-transpose
 *  handles the flip) but the index order is now clockwise — front faces would
 *  cull. Swapping the 2nd/3rd index of every triangle restores CCW fronts.
 *  Non-indexed geometry gets a trivial sequential index first so the swap can
 *  happen in place. geometry.groups are untouched (swaps stay inside a triangle). */
function flipWinding(geo: THREE.BufferGeometry): void {
  if (!geo.index) {
    const count = geo.attributes.position.count;
    const arr = count > 65535 ? new Uint32Array(count) : new Uint16Array(count);
    for (let i = 0; i < count; i++) arr[i] = i;
    geo.setIndex(new THREE.BufferAttribute(arr, 1));
  }
  const index = geo.index!;
  for (let i = 0; i + 2 < index.count; i += 3) {
    const b = index.getX(i + 1);
    index.setX(i + 1, index.getX(i + 2));
    index.setX(i + 2, b);
  }
  index.needsUpdate = true;
}

export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly rig: CameraRig;
  private container: HTMLElement;
  private profile: DeviceProfile;
  private assembly: AssemblyResult | null = null;
  private contactShadow: ContactShadow | null = null;
  /** Studio lighting path (opt-in via viewer.studioLighting): fixed key light +
   *  real-time self-shadow + studio contact shadow + per-colorway color grade. When
   *  set, the legacy headlight/ContactShadow are NOT created. */
  private studio: StudioLighting | null = null;
  /** Remembered for studio per-colorway GLB swaps (applyFabric rebuilds with these). */
  private lastProduct: ProductConfig | null = null;
  private lastState: ConfigState | null = null;
  /** Active per-colorway GLB override + studio recipe (studio path). */
  private glbOverride: string | undefined = undefined;
  private currentRecipe: StudioRecipe | undefined = undefined;
  /** Last colorway picked (carries glb + glbByForme + studio). The GLB is resolved
   *  from (colorway, formeId) on every rebuild so size/shape AND colour both drive it. */
  private currentSwatch: FabricSwatch | null = null;
  /** Frames remaining to re-render the contact-shadow depth pass after a rebuild
   *  (the model must be in the scene; a few frames cover texture/material settle). */
  private shadowRefreshFrames = 0;
  private animationHandle = 0;
  private frameMon = new FrameMonitor();
  private resizeObserver: ResizeObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private destroyed = false;
  /** Callbacks run on destroy() — used to stop detached background work (e.g. the
   *  predictive prefetch) so it can't keep loading / leak GPU memory past teardown. */
  private cleanupCallbacks: Array<() => void> = [];
  /** Callbacks fired when a freshly-built assembly is actually displayed. */
  private assemblyShownCallbacks: Array<() => void> = [];
  /** Callbacks fired when the DISPLAYED result changes in any way that AR cares
   *  about — a new shape (rebuild) OR a fabric/leg recolour. Drives background
   *  USDZ/AR regeneration so "View in your space" always matches what's on screen. */
  private contentChangedCallbacks: Array<() => void> = [];
  /** Callbacks fired when a build came back DEGRADED (a declared GLB failed) and
   *  was therefore NOT displayed. The UI uses this to show a non-blocking "couldn't
   *  load" hint while the previous model / cover stays up — never a white box. */
  private assemblyFailedCallbacks: Array<(failedModules: string[]) => void> = [];
  /** Monotonic build id — newest wins. A rebuild whose token is stale by the time
   *  its async build resolves is discarded (the user switched shape mid-load). */
  private rebuildToken = 0;
  /** Single-slot gate for HEAVY (full-quality) GLB loads — see the LOD phase 2
   *  discipline. Chained promise: each full load awaits the previous one. */
  private fullLoadGate: Promise<void> = Promise.resolve();
  /** Last fabric/leg the UI asked for — remembered even before any model exists,
   *  so a tap DURING the cold load isn't a ghost click: every build re-applies
   *  these. Also keeps fabric/leg across a shape switch (the new build inherits
   *  them) instead of reverting to the config default. null until the first pick;
   *  the build then falls back to the state's flat fabric/leg for the first paint. */
  private desiredFabric: FabricSwatch | null = null;
  private desiredLeg: LegOption | null = null;
  /** On-demand rendering: only draw when something actually changed. A static
   *  sofa shouldn't burn GPU 60×/s — critical with 3 simultaneous contexts in
   *  the preview harness (Fork C). Set true by camera moves, rebuilds, resizes. */
  private needsRender = true;
  /** Pause the loop entirely when the canvas is offscreen (harness iframe out of
   *  view) or the tab is hidden. requestAnimationFrame already throttles hidden
   *  tabs, but IntersectionObserver also kills offscreen-but-visible-tab draws. */
  private onscreen = true;
  /** Default camera angle (from the initial config) — restored by resetView()
   *  when leaving a part focus. */
  private defaultYawDeg = 0;
  private defaultPitchDeg = 0;
  /** Reused scratch for projectToScreen — avoids a Vector3 allocation on every call
   *  (9×/frame while Dimensions is open → steady GC pauses during an orbit). */
  private readonly projScratch = new THREE.Vector3();

  constructor(options: EngineOptions) {
    this.container = options.container;
    this.profile = options.profile;
    this.defaultYawDeg = options.initial.yaw;
    this.defaultPitchDeg = options.initial.pitch;
    this.scene = new THREE.Scene();
    this.renderer = createRenderer(options.container, options.profile);
    // KTX2 (GPU-compressed) texture support — inert for non-KTX2 GLBs.
    wireKtx2(this.renderer);
    // GLTFLoader leaves baked textures at anisotropy=1 (blurry at grazing angles);
    // tell Materials the GPU max so every map (baked + runtime) renders crisp.
    setMaxAnisotropy(this.renderer.capabilities.getMaxAnisotropy());
    if (options.viewer?.exposure !== undefined) {
      this.renderer.toneMappingExposure = options.viewer.exposure;
    }
    setupLighting(this.scene, options.profile, this.renderer, {
      environmentIntensity: options.viewer?.environmentIntensity,
    });

    // Blurred-depth contact shadow (model-viewer's ground shadow technique).
    // Sized to each assembly in rebuildAssembly(); only when the profile allows it.
    if (!options.viewer?.studioLighting && options.profile.shadowsEnabled && (options.viewer?.shadowOpacity ?? 0.8) > 0) {
      this.contactShadow = new ContactShadow(this.renderer, this.scene, {
        intensity: options.viewer?.shadowOpacity,
      });
    }

    this.rig = new CameraRig(
      options.initial.yaw,
      options.initial.pitch,
      options.initial.zoom,
      options.container,
      {
        fov: options.viewer?.fov,
        minZoom: options.viewer?.minZoom,
        maxZoom: options.viewer?.maxZoom,
        minPitchDeg: options.viewer?.minPitchDeg,
        maxPitchDeg: options.viewer?.maxPitchDeg,
        target: options.viewer?.cameraTarget,
        frameMargin: options.viewer?.frameMargin,
        fitWidthFraction: options.viewer?.fitWidthFraction,
      },
    );

    // Lighting path. STUDIO (opt-in): fixed key light + real-time self-shadow +
    // studio contact shadow + per-colorway grade — the look Alex tunes in
    // tools/studio. Otherwise the legacy camera-headlight (the Koala/default).
    if (options.viewer?.studioLighting) {
      this.studio = new StudioLighting(this.scene, this.renderer, this.rig.camera);
      this.currentRecipe = options.viewer.studioRecipe;
    } else {
      // Camera headlight: the optional key light is parented to the camera so it
      // tracks the view — the lit aspect of the sofa stays constant as the user
      // rotates (no highlight sliding across a world-fixed light). Off by default.
      addCameraHeadlight(this.scene, this.rig.camera, options.viewer?.keyLightIntensity ?? 0);
    }

    // Module-cache memory budget by device class. Decoded 4K texture sets run
    // ~800 MB per Bungalow model: a phone tab (~1.4 GB) can only afford the
    // displayed model (budget 0 extras — HTTP cache covers re-switches); desktop
    // keeps up to ~1.5 GB of extras for instant back-and-forth.
    setModuleCacheBudget(isMobileDevice() ? 0 : 1_500_000_000);

    // Any camera change must trigger a redraw (and notify the UI). Subscribing
    // unconditionally — even without onCameraChange — is what makes on-demand
    // rendering correct: orbit/zoom flags needsRender so the frame is drawn.
    this.rig.state.subscribe(() => {
      this.needsRender = true;
      options.onCameraChange?.({
        yaw: this.rig.getYawDeg(),
        pitch: this.rig.getPitchDeg(),
        zoom: this.rig.getZoom(),
      });
    });
    if (options.onLowFps) {
      this.frameMon.onLowFps(options.onLowFps);
    }

    this.bindResize();
    this.start();
  }

  /**
   * Build + display the assembly for `state`. Returns the outcome so callers can
   * react honestly:
   *  - "shown": the new model is on screen.
   *  - "superseded": a newer rebuild started mid-load and won — this one was
   *     dropped; the caller must NOT roll back (the newer pick owns the state).
   *  - "failed": a declared GLB failed; nothing was swapped (cover / previous
   *     model kept). The caller should roll its selection back to match the view.
   */
  async rebuildAssembly(product: ProductConfig, state: ConfigState): Promise<"shown" | "superseded" | "failed"> {
    const token = ++this.rebuildToken;
    this.lastProduct = product;
    this.lastState = state;
    // Studio path: resolve the GLB from (active colorway × current forme) so SIZE/SHAPE
    // and COLOUR both pick the right baked mesh (e.g. small 2-seater vs large 2-seater).
    if (this.studio) {
      const sw = this.resolveSwatch(product, state);
      if (sw) {
        this.glbOverride = (sw.glbByForme && sw.glbByForme[state.formeId]) || sw.glb || this.glbOverride;
        if (sw.studio) this.currentRecipe = sw.studio;
      }
    }
    if (typeof window !== "undefined") window.__NEOVAA_SCENE_READY__ = false;
    if (typeof performance !== "undefined") performance.mark("assembly-build-start");

    // ── Progressive LOD (cold load + EVERY switch, all devices) ────────────
    // Load the light proxy (X.lod.glb, ~0.5 MB) first so the NEW model is on
    // screen and spinnable in <1s, then stream the full GLB in behind and swap.
    // On MOBILE this is also a MEMORY discipline: the old "keep the previous
    // model up until the new full is ready" path holds TWO ~800 MB-decoded
    // models transiently and iOS kills the tab ("a problem repeatedly
    // occurred", Alex's iPhone 2026-06-11). Via the proxy, the old full is
    // disposed the moment the tiny proxy shows.
    // DESKTOP switches go through the proxy too (since 2026-06-11 evening):
    // the old keep-the-crisp-model-until-full-arrives behaviour reads as a
    // 4s+ FREEZE on a real network (each 4K full is 4-19 MB) — Alex on prod:
    // "ça reste et après quelques secondes ça change... 4 secondes d'attente
    // minimum". Instant low-poly feedback beats a frozen crisp model.
    const fullUrl = this.glbOverride;
    if (
      this.studio &&
      isLodEnabled() &&
      fullUrl &&
      proxyUrlFor(fullUrl)
    ) {
      await ensureLodManifest(fullUrl);
      const proxyUrl = proxyUrlFor(fullUrl);
      if (token === this.rebuildToken && proxyUrl && hasProxy(fullUrl)) {
        return this.buildAndDisplayProgressive(product, state, token, fullUrl, proxyUrl);
      }
      // No proxy (or superseded mid-manifest) → fall through to the direct build.
      if (token !== this.rebuildToken) return "superseded";
    }

    const next = await buildAssembly(product, state.formeId, this.glbOverride);
    return this.displayBuiltAssembly(next, product, state, token, { isLodFull: false });
  }

  /**
   * Progressive-LOD cold load: build + show the light proxy first (spinnable now),
   * then build + swap the full model and dispose the proxy. Honors latest-wins at
   * every await so a switch during either phase drops the stale work. The proxy
   * phase is what flips __NEOVAA_SCENE_READY__ (first spinnable); the full phase
   * sets stage='full' + __NEOVAA_FULL_AT__ and frees the proxy's GPU resources so
   * proxy + full are never both held (the mobile cache budget holds).
   */
  private async buildAndDisplayProgressive(
    product: ProductConfig,
    state: ConfigState,
    token: number,
    fullUrl: string,
    proxyUrl: string,
  ): Promise<"shown" | "superseded" | "failed"> {
    // The device-effective full tier: on mobile, heavy models (chaise/U — ~2 GB
    // decoded at 4K, over the iOS tab budget ALONE) resolve to their 1024px
    // <name>.m.glb (~135 MB); desktop and light models keep the true 4K full.
    const effFullUrl = effectiveFullUrl(fullUrl, isMobileDevice());

    // Phase 1 — proxy. Build with the proxy GLB overriding every module.
    const proxyBuild = await buildAssembly(product, state.formeId, proxyUrl);
    if (token !== this.rebuildToken) {
      disposeAssembly(proxyBuild);
      return "superseded";
    }
    if (proxyBuild.failedModules.length > 0) {
      // Proxy failed to load — abandon LOD for this build and fall back to the
      // normal direct full load (which has its own failure handling).
      disposeAssembly(proxyBuild);
      const full = await buildAssembly(product, state.formeId, effFullUrl);
      return this.displayBuiltAssembly(full, product, state, token, { isLodFull: false });
    }
    this.announceLodStage("proxy");
    // Display the proxy: this adds it, frames, lights, flips SCENE_READY, and fires
    // onAssemblyShown → main.ts lifts the cover. The model is spinnable NOW.
    this.displayBuiltAssembly(proxyBuild, product, state, token, { isLodFull: false });

    // Phase 2 — full. Heavy-load discipline (the 4-quick-swaps iOS crash, 2026-06-11):
    // (a) SETTLE: only pull the heavy full once no newer rebuild has fired for
    //     FULL_SETTLE_MS. During a swap streak only tiny proxies move — the full
    //     is fetched solely for the model the user actually STAYS on. This is also
    //     what makes the proxy phase responsive: no multi-second parse/decode work
    //     competing with the user's orbit mid-streak.
    // (b) SERIALIZE: at most ONE full download+parse in flight, ever. A stale full
    //     still decodes its ~800 MB of 4K maps BEFORE the token check can drop it;
    //     several in parallel is a multi-GB transient = iOS kills the tab.
    const FULL_SETTLE_MS = 600;
    await new Promise((r) => setTimeout(r, FULL_SETTLE_MS));
    if (token !== this.rebuildToken) return "shown"; // proxy is up; the newer swap owns the screen

    const prevGate = this.fullLoadGate;
    let releaseGate!: () => void;
    this.fullLoadGate = new Promise<void>((r) => (releaseGate = r));
    await prevGate;
    if (token !== this.rebuildToken) {
      releaseGate();
      return "shown"; // a newer swap won while we queued for the heavy-load slot
    }
    let fullBuild: AssemblyResult;
    // Real download progress of the full model → the UI's HD indicator
    // (calibrated to actual bytes, not a fake spinner). Unsubscribed as soon as
    // the build settles, success or not.
    const offProgress = onModuleProgress(effFullUrl, (p) => this.announceLodProgress(p));
    try {
      fullBuild = await buildAssembly(product, state.formeId, effFullUrl);
      if (token !== this.rebuildToken) {
        // A switch won during the full download. Drop the build AND evict its
        // cached GLB BEFORE releasing the heavy-load slot: the LRU's skip-MRU
        // rule would otherwise shield this stale ~0.8-2 GB entry while the next
        // queued full parses — two heavies resident = the iOS tab kill (caught
        // by the probe's bytesBoundedPeak, 2026-06-11).
        disposeAssembly(fullBuild);
        evictModule(effFullUrl);
        trimModuleCache();
        return "superseded";
      }
    } finally {
      offProgress();
      releaseGate();
    }
    if (fullBuild.failedModules.length > 0) {
      // Full failed but the proxy is up + spinnable — keep the proxy (a sharp-
      // enough fallback) rather than blanking. Surface the failure.
      disposeAssembly(fullBuild);
      console.error(
        `[engine] LOD full load for "${state.formeId}" failed (${fullBuild.failedModules.join(", ")}). ` +
          `Keeping the proxy on screen.`,
      );
      this.notifyAssemblyFailed(fullBuild.failedModules);
      return "shown"; // proxy IS shown — not a blank
    }
    // Pre-upload the full model's 4K maps to the GPU WHILE the proxy is still on
    // screen, so the swap lands on a fully-textured model. Without this, three.js
    // uploads maps lazily on first render and the full flashes its bare (white)
    // material for a frame or two — the "white flash" between the low-poly proxy
    // and the finished render.
    this.warmAssemblyTextures(fullBuild);
    // Swap the full in for the proxy (displayBuiltAssembly disposes the current
    // assembly = the proxy group), then free the proxy's cached GLB so proxy + full
    // are never both held. setLodStage('full') records __NEOVAA_FULL_AT__.
    const result = this.displayBuiltAssembly(fullBuild, product, state, token, { isLodFull: true });
    if (result === "shown") {
      evictModule(proxyUrl); // dispose the proxy GLB's geometry + 256px maps now
      this.announceLodStage("full");
      trimModuleCache(); // re-bound after eviction (keeps __NEOVAA_MODULE_CACHE__ honest)
    }
    return result;
  }

  /** Subscribers to progressive-LOD stage changes (UI quality indicator). */
  private lodStageListeners = new Set<(stage: "proxy" | "full") => void>();
  /** Subscribers to the full model's REAL download progress (0..1, -1 unknown). */
  private lodProgressListeners = new Set<(p: number) => void>();

  /** Subscribe to the full model's real download progress during a progressive
   *  load. 0..1 from actual bytes; -1 when content-length is absent. */
  onLodProgress(cb: (p: number) => void): () => void {
    this.lodProgressListeners.add(cb);
    return () => this.lodProgressListeners.delete(cb);
  }

  private announceLodProgress(p: number): void {
    this.lodProgressListeners.forEach((cb) => cb(p));
  }

  /** Subscribe to LOD stage changes: 'proxy' fires when a low-quality proxy goes
   *  on screen (the full model is still streaming), 'full' at the crisp swap.
   *  Lets the UI tell the shopper the HD version is coming instead of silently
   *  showing the rough model (Alex 2026-06-11). Returns an unsubscribe. */
  onLodStage(cb: (stage: "proxy" | "full") => void): () => void {
    this.lodStageListeners.add(cb);
    return () => this.lodStageListeners.delete(cb);
  }

  private announceLodStage(stage: "proxy" | "full"): void {
    setLodStage(stage);
    this.lodStageListeners.forEach((cb) => cb(stage));
  }

  /** Force a freshly-built model's textures onto the GPU BEFORE it is revealed.
   *  three.js uploads maps lazily on first render: the proxy's 256px maps upload
   *  instantly, but the full model's 4K maps take a frame or two, during which the
   *  material renders bare (flat + bright = the "white flash" between the low-poly
   *  proxy and the finished render). Called while the proxy is still on screen, so
   *  the swap lands on an already-textured model. Idempotent, best-effort. */
  private warmAssemblyTextures(build: AssemblyResult): void {
    if (typeof this.renderer.initTexture !== "function") return;
    const seen = new Set<THREE.Texture>();
    const maps = [
      "map",
      "normalMap",
      "roughnessMap",
      "metalnessMap",
      "aoMap",
      "emissiveMap",
    ] as const;
    build.group.traverse((o) => {
      const mat = (o as THREE.Mesh).material;
      if (!mat) return;
      const list = Array.isArray(mat) ? mat : [mat];
      for (const m of list) {
        for (const key of maps) {
          const tex = (m as unknown as Record<string, THREE.Texture | null>)[key];
          if (tex && !seen.has(tex)) {
            seen.add(tex);
            try {
              this.renderer.initTexture(tex);
            } catch {
              /* upload hiccup is non-fatal — falls back to lazy upload on render */
            }
          }
        }
      }
    });
  }

  /**
   * Swap a freshly-built assembly onto the screen: dispose the previous one at the
   * last moment, scale/frame/texture/light it, flip scene-ready, and notify. Shared
   * by the single-phase rebuild AND both phases of the progressive-LOD load.
   */
  private displayBuiltAssembly(
    next: AssemblyResult,
    product: ProductConfig,
    state: ConfigState,
    token: number,
    opts: { isLodFull: boolean },
  ): "shown" | "superseded" | "failed" {
    // Latest-wins: if a newer rebuild started while we awaited (the user tapped a
    // different shape/size during the load), drop this now-stale result and leave
    // whatever is on screen untouched. Disposing the unused build is safe — its
    // GLB resources are cache-owned (skipped by disposeAssembly), so only its own
    // scaffolding is freed; the live assembly's shared geometry is never touched.
    if (token !== this.rebuildToken) {
      disposeAssembly(next);
      return "superseded";
    }

    // Safety net: a build where a DECLARED GLB failed to load is degraded (it
    // contains a procedural placeholder, e.g. the white-box sofa). Refuse to
    // display it — drop it and keep whatever is on screen (the previous good model
    // on a switch, or the cover on first load). Notify the UI so it can surface a
    // non-blocking hint. The user never sees a blank or a wrong placeholder.
    if (next.failedModules.length > 0) {
      disposeAssembly(next);
      console.error(
        `[engine] build for "${state.formeId}" degraded — modules failed: ` +
          `${next.failedModules.join(", ")}. Keeping the current view; not displaying placeholders.`,
      );
      // Re-arm scene-ready: if this was the FIRST build (nothing on screen yet),
      // the cover must stay up, so leave __NEOVAA_SCENE_READY__ false. If a model
      // is already shown, restore the flag we cleared at the top of this call.
      if (typeof window !== "undefined" && this.assembly) {
        window.__NEOVAA_SCENE_READY__ = true;
      }
      this.notifyAssemblyFailed(next.failedModules);
      return "failed";
    }

    // Swap only now that the new build is ready: dispose the previously-displayed
    // assembly at the last moment so the old model stays visible during the build
    // (no empty-viewer flash on an interactive switch — the cover never returns).
    if (this.assembly) disposeAssembly(this.assembly);
    this.assembly = next;
    this.scene.add(this.assembly.group);
    // Pin EVERY GLB the now-displayed assembly clones from (modular products use
    // several; glbOverride products one), so eviction can never free live
    // geometry/maps. Then trim immediately: on mobile (extras budget 0) this is
    // the moment the PREVIOUS model's ~800 MB of decoded textures is released —
    // before, it lingered until the next load and stacked toward the tab kill.
    const liveUrls: Array<string | undefined | null> = [this.glbOverride];
    this.assembly.group.traverse((o) => {
      const u = (o.userData as { glbUrl?: string } | undefined)?.glbUrl;
      if (u) liveUrls.push(u);
    });
    setPinnedModules(liveUrls);
    trimModuleCache();
    if (typeof performance !== "undefined") {
      performance.mark("assembly-build-end");
      try {
        performance.measure("time-to-first-mesh", "assembly-build-start", "assembly-build-end");
      } catch {
        /* mark missing — ignore */
      }
    }

    // (v50) Apply the resolved forme's Size scale to the WHOLE assembly group
    // BEFORE framing, so the camera fit and contact shadow account for it. The
    // scale is the dimension multiplier carried by the Size axis (Regular=1,
    // Large≈1.15); a forme without one falls back to its Size's scale, else 1.
    // Config-driven — no per-product branching. The group is recentred in
    // buildAssembly, so scaleSetScalar keeps it centred on the origin.
    const forme = product.formes.find((f) => f.id === state.formeId);
    const size = product.sizes?.find((s) => s.id === state.sizeId);
    const scale = forme?.scale ?? size?.scale ?? 1;
    // A forme flagged `mirror` renders the left-handed GLB as its right-handed
    // variant via a negative X scale (e.g. left-chaise GLB → right-chaise). three.js
    // flips the winding + normals from the negative determinant, so the normal map
    // shades correctly — unlike baking the mirror into the mesh (breaks tangents).
    this.assembly.group.scale.set(forme?.mirror ? -scale : scale, scale, scale);
    this.assembly.group.updateMatrixWorld(true);
    // Recompute the framing bbox in world space so it reflects the scaled group.
    const scaledBox = new THREE.Box3().setFromObject(this.assembly.group);
    this.assembly.bbox = scaledBox;

    // Reframe the camera to fit the new assembly (layouts vary wildly in size).
    // The LOD full swap reuses the SAME geometry/scale as the proxy, so reframing
    // is a no-op there visually (the camera doesn't jump) — but it's cheap and
    // keeps the contact shadow + studio fit correct.
    this.rig.frameBox(this.assembly.bbox);

    // Prefer the UI's last explicit pick (desiredFabric/Leg) — this is what makes
    // a tap during the load take effect (it was stored even with no model) and
    // survives a shape switch. Fall back to the state's flat fabric/leg for the
    // very first build, before any pick.
    const fabric = this.desiredFabric
      ?? product.fabrics.find((f) => f.id === state.fabricId)
      ?? product.fabrics[0];
    const leg = this.desiredLeg
      ?? product.legs.find((l) => l.id === state.legId)
      ?? product.legs[0];
    if (fabric) applyFabric(this.assembly, fabric);
    if (leg) applyLeg(this.assembly, leg);
    // Crisp baked GLB maps (GLTFLoader defaults them to anisotropy=1).
    maximizeAnisotropy(this.assembly);

    // Re-fit the contact shadow to the new assembly footprint and schedule a few
    // depth-pass refreshes (the model is now in the scene; settle frames cover
    // material/texture readiness so the footprint is captured correctly).
    if (this.contactShadow) {
      this.contactShadow.fit(this.assembly.bbox);
      this.shadowRefreshFrames = 4;
    }
    if (this.studio) {
      this.studio.setAssembly(this.assembly.group);
      // frameBox ran above, so the rig's max dolly-out is current — the room
      // sizes its walls beyond it (camera can never zoom out through a wall).
      this.studio.fit(this.assembly.bbox, this.rig.getMaxDollyDistance());
      if (this.currentRecipe) this.studio.applyRecipe(this.currentRecipe);
      this.shadowRefreshFrames = 4;
    }
    this.needsRender = true;

    if (typeof window !== "undefined") {
      window.__NEOVAA_SCENE_READY__ = true;
      window.__NEOVAA_REBUILD_COUNT__ = (window.__NEOVAA_REBUILD_COUNT__ ?? 0) + 1;
    }
    // The new assembly is fully on screen now (added, scaled, framed, textured),
    // so signal listeners. main.ts uses the FIRST of these to lift the cover and
    // mark the scene ready — driving the reveal off a real displayed model rather
    // than a specific build call, so the cover can never lift on an empty viewer.
    // void opts.isLodFull — kept for clarity / future per-phase hooks.
    void opts;
    this.notifyAssemblyShown();
    this.notifyContentChanged();
    return "shown";
  }

  /** Recolor the scene backdrop (the renderer uses alpha:false, so this is the
   *  canvas background). Used by the mobile UI to blend the viewer with the
   *  active theme. Config-driven — no per-client / per-theme branching here. */
  setBackground(hex: string): void {
    this.scene.background = new THREE.Color(hex);
    this.needsRender = true;
  }

  /** The active colorway swatch: the last UI pick, else the product's default colorway
   *  (so SIZE changes before any colour tap still resolve the right per-forme GLB). */
  private resolveSwatch(
    product: ProductConfig,
    state: ConfigState,
  ): { glb?: string; glbByForme?: Record<string, string>; studio?: StudioRecipe } | null {
    if (this.currentSwatch) return this.currentSwatch;
    const mats = product.materials ?? [];
    // Matiere du state d'abord (m= dans l'URL, 2026-09-07) : sans ca un lien velours terracotta
    // chargeait le GLB jacquard terracotta, premier materiau qui porte ce coloris.
    const want = state.colorwayId ?? state.fabricId;
    const own = mats.find((m) => m.id === state.materialId);
    if (own) {
      const c = own.colorways.find((cw) => cw.id === want) ?? own.colorways[0];
      if (c) return c;
    }
    for (const m of mats) {
      const c = m.colorways.find((cw) => cw.id === state.fabricId) ?? m.colorways[0];
      if (c) return c;
    }
    return null;
  }

  applyFabric(swatch: FabricSwatch): void {
    // Studio path: a colorway carries its own baked GLB(s) + recipe. Remember it and
    // rebuild — rebuildAssembly resolves the GLB from (this colorway × current forme).
    if (this.studio) {
      this.currentSwatch = swatch;
      if (swatch.studio) this.currentRecipe = swatch.studio;
      if (this.lastProduct && this.lastState) {
        void this.rebuildAssembly(this.lastProduct, this.lastState);
      } else if (this.currentRecipe) {
        this.studio.applyRecipe(this.currentRecipe);
        this.needsRender = true;
      }
      this.notifyContentChanged();
      return;
    }
    // Remember the pick even if no model exists yet (tap during cold load), so the
    // next build applies it instead of dropping it as a ghost click.
    this.desiredFabric = swatch;
    if (this.assembly) applyFabric(this.assembly, swatch);
    this.needsRender = true;
    this.notifyContentChanged();
  }

  applyLeg(leg: LegOption): void {
    this.desiredLeg = leg;
    if (this.assembly) applyLeg(this.assembly, leg);
    this.needsRender = true;
    this.notifyContentChanged();
  }

  /** Recolor a named part of the live model — drives generic config-declared swatch
   *  rows (e.g. a lamp's shade-color / base-finish). Matches meshes/materials by name
   *  substring. No-op if nothing is built yet. */
  recolorPart(target: string, color: string): void {
    if (this.assembly) recolorByName(this.assembly, target, color);
    this.needsRender = true;
    this.notifyContentChanged();
  }

  /** Approximate model dimensions (scene units) from the current assembly. */
  getDimensions(): { w: number; h: number; d: number } | null {
    if (!this.assembly) return null;
    const size = this.assembly.bbox.getSize(new THREE.Vector3());
    return { w: size.x, h: size.y, d: size.z };
  }

  /** Current assembly world-space bounding box (min/max) for the dimensions
   *  overlay. Unlike getDimensions (sizes only), this gives the corner points the
   *  measurement lines are anchored to. null when no model is built. */
  getAssemblyBox(): { min: THREE.Vector3; max: THREE.Vector3 } | null {
    if (!this.assembly) return null;
    return { min: this.assembly.bbox.min.clone(), max: this.assembly.bbox.max.clone() };
  }

  /** Convert a normalised bbox anchor (0..1 per axis) to a world point using the
   *  current assembly box. Drives hotspot dot placement + part focus so a region
   *  stays correctly placed across shape/size switches. null when no model. */
  anchorToWorld(anchor: { x: number; y: number; z: number }): THREE.Vector3 | null {
    if (!this.assembly) return null;
    const { min, max } = this.assembly.bbox;
    return new THREE.Vector3(
      min.x + (max.x - min.x) * anchor.x,
      min.y + (max.y - min.y) * anchor.y,
      min.z + (max.z - min.z) * anchor.z,
    );
  }

  /** Focus the camera on a product part (the connected Details mode): frame a
   *  sub-region around the part's anchor and glide to a pleasant pose. */
  focusOnPart(part: HotspotDef, durationMs = 650): void {
    if (!this.assembly) return;
    const world = this.anchorToWorld(part.anchor);
    if (!world) return;
    const size = this.assembly.bbox.getSize(new THREE.Vector3());
    // Frame ~38% of the largest dimension: close enough to feature the part, wide
    // enough to keep spatial context.
    const radius = Math.max(size.x, size.y, size.z) * 0.38;
    this.rig.focusRegion(world, radius, part.focus?.yawDeg, part.focus?.pitchDeg, durationMs);
    this.needsRender = true;
  }

  /** Focus on a normalised-anchor region with a tunable tightness. Like focusOnPart
   *  but the focal-view buttons want a closer crop so the part actually fills the
   *  frame (focusOnPart's 0.38 keeps the whole sofa in view). */
  focusOnRegion(anchor: { x: number; y: number; z: number }, focus?: { yawDeg?: number; pitchDeg?: number }, capMeters = 0.3, durationMs = 650): void {
    if (!this.assembly) return;
    const world = this.anchorToWorld(anchor);
    if (!world) return;
    const size = this.assembly.bbox.getSize(new THREE.Vector3());
    // Cap the radius so a focus zooms to a PART, not the whole shape — without the cap a
    // big forme (corner / U-shape) gets a huge radius and barely zooms in. The cap is
    // per-view: tight (~one seat) for Left/Right/Corner, looser for the long chaise which
    // is a long element that needs a wider frame to read as a "long chair".
    const radius = Math.min(Math.max(size.x, size.y, size.z) * 0.26, capMeters);
    this.rig.focusRegion(world, radius, focus?.yawDeg, focus?.pitchDeg, durationMs);
    this.needsRender = true;
  }

  /** Re-center the camera on a normalised-anchor part WITHOUT changing the orbit
   *  angle — the named part simply becomes the centre of the frame with a light
   *  dolly-in. Simpler than focusOnRegion (no per-view angle/zoom). A tap on the
   *  viewer afterwards returns to the normal framing (CameraRig tap-to-reset). */
  centerOnRegion(anchor: { x: number; y: number; z: number }, distanceFactor = 0.72, durationMs = 600): void {
    if (!this.assembly) return;
    const world = this.anchorToWorld(anchor);
    if (!world) return;
    this.rig.centerOn(world, distanceFactor, durationMs);
    this.needsRender = true;
  }

  /** The forme id behind what's currently on screen (what the engine last built),
   *  read straight from live state — never a possibly-stale DOM query. */
  get currentFormeId(): string | undefined {
    return this.lastState?.formeId;
  }

  /** Return the camera to the default full-product framing. */
  resetView(durationMs = 600): void {
    this.rig.resetView(this.defaultYawDeg, this.defaultPitchDeg, durationMs);
    this.needsRender = true;
  }

  /** Dolly OUT to fit the whole item (plus slack) without changing the current
   *  orbit angle — used when Dimensions opens so the measurement labels, which sit
   *  outside the bounding box, all fit inside the viewer. */
  zoomOutToFit(factor = 1.4, durationMs = 600): void {
    this.rig.zoomToFit(factor, durationMs);
    this.needsRender = true;
  }

  /** Dolly back to the big default framing (Dimensions closed). Keeps orbit angle. */
  restoreHome(durationMs = 600): void {
    this.rig.returnHome(durationMs);
    this.needsRender = true;
  }

  /**
   * Serialize the CURRENT assembly (this exact shape + fabric + colour + legs, as
   * displayed) to a single binary GLB blob — the one file AR needs. The
   * configurator builds the sofa from several module GLBs at runtime, so there is
   * no single file to hand to AR; this packages the live result into one. Dynamic
   * import keeps GLTFExporter off the initial bundle (AR is rarely the first tap).
   *
   * SIZE (the part that bit us): a naive parse produced 78-104 MB and model-viewer
   * couldn't open it, because GLTFExporter defaults to `maxTextureSize: Infinity`
   * (re-encodes every texture at full 3072px) and `image/png` (lossless). Fix —
   * the two built-in levers (confirmed in GLTFExporter.js: processImage clamps to
   * `options.maxTextureSize` line ~1218; mimeType is read from
   * `texture.userData.mimeType` line ~1168):
   *   1. downscale every texture to `maxTextureSize` (1024 is plenty for AR viewed
   *      at arm's length — on-screen 3D keeps the full 3K, only this AR copy shrinks).
   *   2. force JPEG on the heavy OPAQUE colour/ORM/emissive maps (≈10-30× smaller
   *      than PNG for photographic weave). Normal maps stay PNG (JPEG ringing on a
   *      normal map shows as shading noise). Textures on transparent materials stay
   *      PNG too (JPEG has no alpha).
   * Tagging `userData.mimeType` on the cache-shared textures is render-inert (only
   * the exporter reads it) and idempotent. Returns null if nothing is built.
   */
  async exportAssemblyGLB(maxTextureSize = 1024): Promise<Blob | null> {
    if (!this.assembly) return null;
    const { GLTFExporter } = await import("three/examples/jsm/exporters/GLTFExporter.js");

    // Tag heavy opaque maps as JPEG so the exporter encodes them compressed.
    // Slots: baseColor (map), ORM (ao/roughness/metalness), emissive. NOT normalMap.
    this.assembly.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        const sm = m as THREE.MeshStandardMaterial | undefined;
        if (!sm) continue;
        const opaque = !sm.transparent && (sm.alphaTest ?? 0) === 0;
        if (!opaque) continue; // keep PNG (alpha) on transparent materials
        for (const t of [sm.map, sm.aoMap, sm.roughnessMap, sm.metalnessMap, sm.emissiveMap]) {
          if (t) (t.userData ??= {}).mimeType = "image/jpeg";
        }
      }
    });

    const exporter = new GLTFExporter();
    // binary:true → .glb (model-viewer + Scene Viewer + Quick Look accept GLB). The
    // group's world transform (size scale + recentre) is included so AR scale is
    // correct. maxTextureSize is THE size lever (3072 → 1024 = ~9× fewer pixels).
    const result = await exporter.parseAsync(this.assembly.group, {
      binary: true,
      onlyVisible: true,
      maxTextureSize,
    });
    return new Blob([result as ArrayBuffer], { type: "model/gltf-binary" });
  }

  /**
   * Serialize the CURRENT assembly (this exact shape + fabric + colour + legs, as
   * displayed) to a single USDZ blob for iOS Quick Look AR — the one file iOS
   * needs. This is the SAME conversion <model-viewer> did invisibly: iOS cannot
   * open a GLB in AR, so the GLB is converted to USDZ. Doing it in-browser from
   * the live scene means AR always matches what the shopper configured, and a NEW
   * product needs ZERO pre-baked files or config — drop a GLB, AR just works.
   *
   * three's USDZExporter only understands MeshStandardMaterial (which all our
   * fabric/leg materials are). Dynamic import keeps it off the initial bundle (AR
   * is rarely the first interaction). maxTextureSize caps the heaviest cost so the
   * USDZ stays small enough to download fast on the AR tap. Returns null if
   * nothing is built yet.
   */
  /** Absolute URL of the GLB behind what's ON SCREEN (live colorway × forme ×
   *  device tier). Drives Android Scene Viewer + the desktop AR QR so AR always
   *  shows the CONFIGURED product — never the config-time base model (Alex
   *  2026-06-11). Prefers the destined full/mobile tier over a transient proxy. */
  getLiveGlbUrl(): string | null {
    let url: string | null = this.glbOverride
      ? effectiveFullUrl(this.glbOverride, isMobileDevice())
      : null;
    if (!url && this.assembly) {
      this.assembly.group.traverse((o) => {
        const u = (o.userData as { glbUrl?: string } | undefined)?.glbUrl;
        if (u && !url) url = u;
      });
    }
    if (!url) return null;
    try { return new URL(url, window.location.href).toString(); } catch { return url; }
  }

  async exportAssemblyUSDZ(maxTextureSize = 1024): Promise<Blob | null> {
    if (!this.assembly) return null;
    // Instrumented so the stability probe can assert NO background export ever
    // fires on a mobile journey (the cold-load crash contributor, 2026-06-11).
    if (typeof window !== "undefined") {
      const w = window as unknown as { __NEOVAA_USDZ_EXPORTS__?: number };
      w.__NEOVAA_USDZ_EXPORTS__ = (w.__NEOVAA_USDZ_EXPORTS__ ?? 0) + 1;
    }
    const { USDZExporter } = await import("three/examples/jsm/exporters/USDZExporter.js");
    const exporter = new USDZExporter();
    // USDZ cannot represent negative scales — three's exporter writes the matrix
    // as-is (console.warn only) and Quick Look keeps the original winding, so any
    // mirrored mesh renders INSIDE-OUT in AR (Alex's iPhone, 2026-06-11). Negative
    // determinants exist in two places: formes flagged `mirror` (right-chaise =
    // left GLB × scale.x=-1 on the group) and U-shape GLBs whose right-arm node
    // carries scale.x=-1 internally. When any visible mesh is mirrored, export a
    // flattened clone with the world transform BAKED into the vertices (positions
    // + normals via applyMatrix4, winding flipped back to front-facing) so the
    // USDZ holds a true mirrored mesh and only identity transforms. Clones are
    // disposed right after — no residency cost beyond the export itself.
    this.assembly.group.updateMatrixWorld(true);
    let hasMirror = false;
    this.assembly.group.traverseVisible((o) => {
      if ((o as THREE.Mesh).isMesh && o.matrixWorld.determinant() < 0) hasMirror = true;
    });
    let exportRoot: THREE.Object3D = this.assembly.group;
    const bakedGeometries: THREE.BufferGeometry[] = [];
    if (hasMirror) {
      const flat = new THREE.Group();
      this.assembly.group.traverseVisible((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        const geo = (mesh.geometry as THREE.BufferGeometry).clone();
        geo.applyMatrix4(mesh.matrixWorld);
        if (mesh.matrixWorld.determinant() < 0) flipWinding(geo);
        bakedGeometries.push(geo);
        flat.add(new THREE.Mesh(geo, mesh.material));
      });
      flat.updateMatrixWorld(true);
      exportRoot = flat;
    }
    // AR brightness lift: iOS Quick Look lights the model with its OWN (neutral, dimmer)
    // environment and ignores scene lights, so the 360's punchy HDRI look is lost and AR
    // can read dark. Temporarily raise each material's EMISSIVE (self-illumination) — keyed
    // by the fabric colour map so the weave stays visible — which Quick Look DOES render.
    // Restored in `finally` so the on-screen 3D and the (separate, pre-rendered) 360 are
    // never affected. Tunable via AR_EMISSIVE (validate on a real iPhone).
    // AR lighting: let Quick Look's OWN studio environment do the SHADING (real dimension,
    // not a flat fill) — but the model is too dark under that neutral light. So brighten the
    // base colour (albedo): mapped materials get a brightened copy of their colour map, solid
    // ones get a brighter colour; plus a small warm emissive FLOOR so it's never dim. All
    // restored in `finally` → the on-screen 3D and the (separate) 360 are untouched. Two knobs.
    const AR_ALBEDO_GAIN = 1.5;                // base-colour brightness ×
    const AR_EMISSIVE = 0.12;                  // small warm self-lit floor
    const AR_WARM = new THREE.Color(0xfff1e3);
    const arTouched: {
      m: THREE.MeshStandardMaterial; e: THREE.Color; ei: number; em: THREE.Texture | null;
      map: THREE.Texture | null; col: THREE.Color;
    }[] = [];
    const arDisposable: THREE.Texture[] = [];
    const brighten = (tex: THREE.Texture): THREE.Texture | null => {
      const img = tex.image as { width?: number; height?: number } | undefined;
      const w = img?.width, h = img?.height;
      if (!img || !w || !h) return null;
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      const cx = c.getContext("2d"); if (!cx) return null;
      cx.filter = `brightness(${AR_ALBEDO_GAIN})`;
      cx.drawImage(img as CanvasImageSource, 0, 0);
      const t = new THREE.CanvasTexture(c);
      t.wrapS = tex.wrapS; t.wrapT = tex.wrapT; t.flipY = tex.flipY; t.colorSpace = tex.colorSpace;
      t.needsUpdate = true; arDisposable.push(t);
      return t;
    };
    exportRoot.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mm of mats) {
        const m = mm as THREE.MeshStandardMaterial;
        if (!m?.isMeshStandardMaterial || arTouched.some((t) => t.m === m)) continue;
        arTouched.push({ m, e: m.emissive.clone(), ei: m.emissiveIntensity, em: m.emissiveMap, map: m.map, col: m.color.clone() });
        if (m.map) { const b = brighten(m.map); if (b) m.map = b; }
        else m.color.multiply(AR_WARM).multiplyScalar(AR_ALBEDO_GAIN);
        m.emissive = AR_WARM.clone();
        m.emissiveMap = m.map ?? null;
        m.emissiveIntensity = AR_EMISSIVE;
        m.needsUpdate = true;
      }
    });
    try {
      // quickLookCompatible: true emits the material variant iOS Quick Look needs;
      // the world transform (size scale + recentre) is preserved — on the group
      // for the plain path, baked into the vertices for the mirrored path — so AR
      // scale is correct in the room.
      const arr = (await exporter.parseAsync(exportRoot, {
        maxTextureSize,
        quickLookCompatible: true,
      })) as Uint8Array;
      // Copy into a fresh ArrayBuffer-backed view so the Blob part type is exactly
      // ArrayBuffer (not ArrayBufferLike/SharedArrayBuffer, which TS rejects).
      const bytes = new Uint8Array(arr.byteLength);
      bytes.set(arr);
      return new Blob([bytes.buffer], { type: "model/vnd.usdz+zip" });
    } finally {
      for (const t of arTouched) {
        t.m.emissive.copy(t.e); t.m.emissiveIntensity = t.ei; t.m.emissiveMap = t.em;
        t.m.map = t.map; t.m.color.copy(t.col); t.m.needsUpdate = true;
      }
      for (const t of arDisposable) t.dispose();
      for (const geo of bakedGeometries) geo.dispose();
    }
  }

  /**
   * Capture a still of the CURRENT configured model as a PNG data URL — used for
   * the add-to-cart preview image (the build the shopper just configured). The main
   * canvas runs with preserveDrawingBuffer:false, so we render to an OFFSCREEN target
   * and read the pixels back, which works regardless of the on-demand render state.
   * Square, transparent background (model + contact shadow), so the host can drop it
   * on any card/cart surface. Returns null if nothing is built yet.
   */
  captureSnapshot(size = 900): string | null {
    if (!this.assembly) return null;
    const renderer = this.renderer;
    const camera = this.rig.camera as THREE.PerspectiveCamera;
    const target = new THREE.WebGLRenderTarget(size, size, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      type: THREE.UnsignedByteType,
    });
    const prevAspect = camera.aspect;
    const prevBg = this.scene.background;
    const prevClear = new THREE.Color();
    renderer.getClearColor(prevClear);
    const prevAlpha = renderer.getClearAlpha();
    try {
      camera.aspect = 1;
      camera.updateProjectionMatrix();
      this.scene.background = null; // transparent — host composites on its own surface
      renderer.setRenderTarget(target);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      renderer.render(this.scene, camera);
      const buf = new Uint8Array(size * size * 4);
      renderer.readRenderTargetPixels(target, 0, 0, size, size, buf);
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      // WebGL reads bottom-up; flip rows into the 2D canvas.
      const img = ctx.createImageData(size, size);
      for (let y = 0; y < size; y++) {
        const src = (size - 1 - y) * size * 4;
        img.data.set(buf.subarray(src, src + size * 4), y * size * 4);
      }
      ctx.putImageData(img, 0, 0);
      return canvas.toDataURL("image/png");
    } catch (e) {
      console.warn("[engine] snapshot failed", e);
      return null;
    } finally {
      camera.aspect = prevAspect;
      camera.updateProjectionMatrix();
      this.scene.background = prevBg;
      renderer.setRenderTarget(null);
      renderer.setClearColor(prevClear, prevAlpha);
      target.dispose();
      this.needsRender = true; // redraw the live view after borrowing the renderer
    }
  }

  /**
   * Capture the loading-cover still: the model at the CURRENT camera framing (not
   * square — uses the live canvas aspect so it pixel-aligns with the first real frame)
   * on a TRANSPARENT background, so one image works on every theme (the theme bg shows
   * through from the canvas/CSS behind). Same render path + recipe as the live view, so
   * there is ZERO tone seam when the cover fades to the 3D. Returns a PNG data URL.
   */
  captureCover(): string | null {
    if (!this.assembly) return null;
    const renderer = this.renderer;
    const camera = this.rig.camera as THREE.PerspectiveCamera;
    const w = Math.max(2, Math.round(renderer.domElement.width));
    const h = Math.max(2, Math.round(renderer.domElement.height));
    const target = new THREE.WebGLRenderTarget(w, h, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      type: THREE.UnsignedByteType,
    });
    // Without this the renderer writes LINEAR colour to the target (the sRGB output
    // encode only happens on the canvas pass), so the readback comes out dark/brown.
    // Tagging the target sRGB makes the encode run → the still matches the live view.
    target.texture.colorSpace = THREE.SRGBColorSpace;
    const prevBg = this.scene.background;
    const prevClear = new THREE.Color();
    renderer.getClearColor(prevClear);
    const prevAlpha = renderer.getClearAlpha();
    try {
      this.scene.background = null; // transparent → composites over the theme bg, any theme
      renderer.setRenderTarget(target);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      renderer.render(this.scene, camera); // live camera = live aspect + framing
      const buf = new Uint8Array(w * h * 4);
      renderer.readRenderTargetPixels(target, 0, 0, w, h, buf);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      const img = ctx.createImageData(w, h);
      for (let y = 0; y < h; y++) {
        const src = (h - 1 - y) * w * 4; // WebGL is bottom-up; flip rows
        img.data.set(buf.subarray(src, src + w * 4), y * w * 4);
      }
      ctx.putImageData(img, 0, 0);
      return canvas.toDataURL("image/png");
    } catch (e) {
      console.warn("[engine] cover capture failed", e);
      return null;
    } finally {
      this.scene.background = prevBg;
      renderer.setRenderTarget(null);
      renderer.setClearColor(prevClear, prevAlpha);
      target.dispose();
      this.needsRender = true;
    }
  }

  /** Project a world point to viewer-canvas pixel coords (top-left origin) for DOM
   *  overlays (dimension lines/labels, hotspots). `behind` flags points behind the
   *  camera so callers can hide them. Mirrors model-viewer's slot positioning. */
  projectToScreen(p: THREE.Vector3): { x: number; y: number; behind: boolean } {
    const v = this.projScratch.copy(p).project(this.rig.camera);
    const rect = this.container.getBoundingClientRect();
    return {
      x: (v.x * 0.5 + 0.5) * rect.width,
      y: (-v.y * 0.5 + 0.5) * rect.height,
      behind: v.z > 1,
    };
  }

  /** Subscribe to camera changes (orbit/zoom/reframe) — drives overlay redraws
   *  without a permanent RAF loop. Returns an unsubscribe fn. */
  onCameraChange(cb: () => void): () => void {
    return this.rig.state.subscribe(cb);
  }

  /**
   * Plain-JSON snapshot of the current assembly, consumed by the E2E suite.
   * Reports per-module bbox / vertex count / GLB origin, plus global counters.
   * Returning JSON-only (no Three.js refs) lets Playwright .evaluate() serialise
   * it across the runtime boundary.
   */
  inspectScene(formeId: string): SceneInspection {
    if (!this.assembly) {
      return { formeId, modules: [], gltfLoads: 0, gltfFallbacks: 0 };
    }
    const cam = this.rig.camera;
    cam.updateMatrixWorld(true);
    const proj = new THREE.Matrix4().multiplyMatrices(
      cam.projectionMatrix,
      cam.matrixWorldInverse,
    );
    const modules: SceneInspection["modules"] = [];
    for (const child of this.assembly.group.children) {
      const moduleId = (child.userData?.moduleId as string | undefined)
        ?? `unnamed-${modules.length}`;
      child.updateMatrixWorld(true);
      const meshes: SceneInspection["modules"][number]["meshes"] = [];
      let totalVertices = 0;
      child.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        if (!m.geometry?.attributes?.position) return;
        const wp = new THREE.Vector3();
        m.getWorldPosition(wp);
        const bbox = new THREE.Box3().setFromObject(m);
        const size = bbox.getSize(new THREE.Vector3());
        const ndc = wp.clone().applyMatrix4(proj);
        const inFrustum =
          Number.isFinite(ndc.x) &&
          Math.abs(ndc.x) <= 1 &&
          Math.abs(ndc.y) <= 1 &&
          ndc.z >= -1 &&
          ndc.z <= 1;
        const vertexCount = m.geometry.attributes.position.count;
        totalVertices += vertexCount;
        meshes.push({
          vertexCount,
          worldPos: [wp.x, wp.y, wp.z],
          bboxSize: [size.x, size.y, size.z],
          inFrustum,
          fromGLB: !!m.userData?.fromGLB,
        });
      });
      const mb = new THREE.Box3().setFromObject(child);
      // The CHECK in the spec asks for distinct "module centers" — what we
      // really mean is "placement intent", i.e. the GLB group origin in world
      // space, not the bbox centroid (which drifts based on each GLB's own
      // origin offset). Using group origin makes the test robust to authoring
      // quirks of imported kits.
      const groupPos = new THREE.Vector3();
      child.getWorldPosition(groupPos);
      modules.push({
        moduleId,
        worldPos: [groupPos.x, groupPos.y, groupPos.z],
        bbox: {
          min: [mb.min.x, mb.min.y, mb.min.z],
          max: [mb.max.x, mb.max.y, mb.max.z],
        },
        meshCount: meshes.length,
        totalVertices,
        meshes,
      });
    }
    const winLoads = typeof window !== "undefined" ? window.__NEOVAA_GLTF_LOADS__ : 0;
    const winFallbacks = typeof window !== "undefined" ? window.__NEOVAA_GLTF_FALLBACKS__ : 0;
    return {
      formeId,
      modules,
      gltfLoads: winLoads ?? 0,
      gltfFallbacks: winFallbacks ?? 0,
    };
  }

  /** Register a callback to run on destroy(). Used to cancel detached work that
   *  outlives a single rebuild — e.g. the predictive prefetch loop — so an SPA
   *  unmount / iframe re-init can't leak background loads or GPU textures. */
  onDestroy(cb: () => void): void {
    this.cleanupCallbacks.push(cb);
  }

  /** Register a callback fired each time a freshly-built assembly is added to the
   *  scene (i.e. actually displayed). Used by the boot to drive the cover fade /
   *  scene-ready off the displayed build rather than a specific build call. */
  onAssemblyShown(cb: () => void): void {
    this.assemblyShownCallbacks.push(cb);
  }

  private notifyAssemblyShown(): void {
    for (const cb of this.assemblyShownCallbacks) {
      try {
        cb();
      } catch (e) {
        console.warn("[engine] assembly-shown callback threw", e);
      }
    }
  }

  /** Register a callback fired whenever the displayed result changes (new shape,
   *  fabric, or leg). Used by the AR controller to regenerate the USDZ in the
   *  background so it always reflects the on-screen combination. */
  onContentChanged(cb: () => void): void {
    this.contentChangedCallbacks.push(cb);
  }

  private notifyContentChanged(): void {
    for (const cb of this.contentChangedCallbacks) {
      try {
        cb();
      } catch (e) {
        console.warn("[engine] content-changed callback threw", e);
      }
    }
  }

  /** Register a callback fired when a build was rejected as degraded (a declared
   *  GLB failed). Receives the failed module ids. Used to show a load-failure hint
   *  without ever displaying the placeholder. */
  onAssemblyFailed(cb: (failedModules: string[]) => void): void {
    this.assemblyFailedCallbacks.push(cb);
  }

  private notifyAssemblyFailed(failedModules: string[]): void {
    for (const cb of this.assemblyFailedCallbacks) {
      try {
        cb(failedModules);
      } catch (e) {
        console.warn("[engine] assembly-failed callback threw", e);
      }
    }
  }

  /** Adjust rendering quality at runtime (e.g. on sustained low-fps). */
  degradeQuality(): void {
    this.profile = {
      ...this.profile,
      pixelRatio: Math.max(1, this.profile.pixelRatio - 0.25),
      shadowsEnabled: false,
      antialias: false,
    };
    this.renderer.setPixelRatio(this.profile.pixelRatio);
    this.renderer.shadowMap.enabled = false;
    // KEEP the contact shadow. Once captured it's just a static textured plane (≈0
    // per-frame cost; only the bounded post-rebuild depth pass costs anything), so
    // disposing it on a brief FPS dip is what made the sofa "lose its shadow". Drop
    // pixelRatio + AA to recover FPS instead; the grounding shadow stays.
    this.needsRender = true;
    console.info("[Engine] Quality degraded due to low FPS", this.profile);
  }

  private bindResize() {
    // ResizeObserver drives the resize (instead of probing getBoundingClientRect
    // every frame, which forced a layout reflow per frame per context). A size
    // change both resizes and flags a redraw.
    this.resizeObserver = new ResizeObserver(() => {
      const resized = resizeRendererToDisplaySize(this.renderer, this.rig.camera, this.container);
      // When the aspect actually changed, re-fit the framing — otherwise the model
      // stays framed for the OLD aspect and reads too small. This fixes the "tiny
      // sofa on first load": the engine inits at the desktop viewport size, then the
      // mobile UI re-parents #viewer into the smaller hero (a resize), so re-framing
      // here keeps the fit correct without needing a manual shape toggle.
      if (resized && this.assembly) this.rig.frameBox(this.assembly.bbox);
      this.needsRender = true;
    });
    this.resizeObserver.observe(this.container);

    // Pause the loop when the canvas scrolls offscreen. Decisive for the 3-iframe
    // harness: only the visible phone(s) draw. Browsers already throttle hidden
    // *tabs* via rAF; this also covers offscreen-within-a-visible-tab.
    if (typeof IntersectionObserver !== "undefined") {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          const wasOnscreen = this.onscreen;
          this.onscreen = entries.some((e) => e.isIntersecting);
          if (this.onscreen && !wasOnscreen) this.needsRender = true; // repaint on re-entry
        },
        { threshold: 0 },
      );
      this.intersectionObserver.observe(this.container);
    }
  }

  private start() {
    const tick = (now: number) => {
      if (this.destroyed) return;
      // On-demand render: skip the draw entirely when nothing changed or the
      // canvas is offscreen / tab hidden. The rAF loop keeps spinning (cheap, no
      // GL work) so a later camera move or resize repaints immediately. This is
      // the core GPU/battery/jank optimization — a static configurator draws 0
      // frames instead of 60/s, and 3 harness contexts don't all burn at once.
      const visible = this.onscreen && (typeof document === "undefined" || !document.hidden);
      // Refresh the contact-shadow depth texture for a few frames after a rebuild.
      // Drawn even on otherwise-static frames so the footprint settles, then idles.
      if (this.shadowRefreshFrames > 0 && visible && (this.contactShadow || this.studio)) {
        if (this.contactShadow) this.contactShadow.render();
        if (this.studio) this.studio.render();
        this.shadowRefreshFrames--;
        this.needsRender = true;
      }
      if (this.needsRender && visible) {
        this.frameMon.tick(now);
        this.renderer.render(this.scene, this.rig.camera);
        this.needsRender = false;
        if (typeof window !== "undefined") {
          window.__NEOVAA_FRAME_COUNT__ = (window.__NEOVAA_FRAME_COUNT__ ?? 0) + 1;
        }
      }
      this.animationHandle = requestAnimationFrame(tick);
    };
    this.animationHandle = requestAnimationFrame(tick);
  }

  destroy(): void {
    this.destroyed = true;
    // Stop detached background work (prefetch) FIRST, before freeing caches, so
    // a late-resolving prefetch can't repopulate a just-cleared cache.
    for (const cb of this.cleanupCallbacks) {
      try {
        cb();
      } catch {
        /* a cleanup throwing must not block the rest of teardown */
      }
    }
    this.cleanupCallbacks = [];
    this.assemblyShownCallbacks = [];
    this.assemblyFailedCallbacks = [];
    this.desiredFabric = null;
    this.desiredLeg = null;
    cancelAnimationFrame(this.animationHandle);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.rig.destroy();
    if (this.contactShadow) {
      this.contactShadow.dispose();
      this.contactShadow = null;
    }
    if (this.studio) {
      this.studio.dispose();
      this.studio = null;
    }
    if (this.assembly) {
      disposeAssembly(this.assembly);
      this.assembly = null;
    }
    clearTextureCache();
    clearModuleCache();
    this.renderer.dispose();
    if (this.renderer.domElement.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }
  }
}
